import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { func, arrayOf, shape, bool, string } from "prop-types";
import TextButton from "@shared/v2/TextButton";
import Plus from "@shared/v2/Icomoon/Icons/Plus";
import Dropdown from "@shared/v2/Dropdown/Dropdown";
import { CheckboxOption, CheckboxGroupHeading } from "@shared/v2/Dropdown/custom/MultiComponents";
import ContactInfoCard from "./ContactInfoCard";
import { mapPeople, mapRole, generateRandomId } from "../model/ConactOptions";
import AddAdditionalContact from "./AddAdditionalContact";

const Contacts = ({
  collaborators,
  team,
  clients,
  roles,
  setContact,
  contacts,
  setNewContacts,
  newContacts,
  setAddContact,
  addContact,
  contactType,
}) => {
  const [newAddedContact, setNewAddedContact] = useState({});
  const [isEditFormOpen, setIsEditFormOpen] = useState({});
  const [editingContact, setEditiongContact] = useState({});

  const ContactsOptions = () => {
    return [
      ...(contactType === "additional" || contactType === "seller"
        ? [{ label: "Clients", options: mapPeople(clients, "Clients") }]
        : []),
      ,
      ...(contactType === "additional" || contactType === "co-op"
        ? [{ label: "Collaborators", options: mapPeople(collaborators, "Collaborators") }]
        : []),
      ,
      ...(contactType === "additional" || contactType === "co-op"
        ? [{ label: "Team Members", options: mapPeople(team, "Team Members") }]
        : []),
    ];
  };

  const roleOptions = [
    ...(contactType === "additional" || contactType === "seller"
      ? [{ label: "Clients", options: mapRole(roles.client, "Clients") }]
      : []),
    ...(contactType !== "seller"
      ? [
          { label: "Collaborator Roles", options: mapRole(roles.collaborator, "Collaborators") },
          { label: "Team Members", options: mapRole(roles.team_member, "Team Members") },
        ]
      : []),
  ].map((roleGroup) => ({
    ...roleGroup,
    options: roleGroup.options.filter(
      (option) =>
        ![...newContacts, newAddedContact, editingContact].some(
          (person) => person?.role?.value === option.value,
        ),
    ),
  }));

  const formatContactData = (newAddedContact) => {
    if (!newAddedContact) return null;

    return {
      label: `${newAddedContact.firstName} ${newAddedContact.lastName}${
        newAddedContact.role?.label ? ` (${newAddedContact.role.label})` : ""
      }`,
      name: `${newAddedContact.firstName} ${newAddedContact.lastName}`,
      firstName: newAddedContact.firstName,
      lastName: newAddedContact.lastName,
      value: newAddedContact.id || generateRandomId(),
      roleLabel: newAddedContact.role?.label,
      role: newAddedContact.role,
      avatarImg: newAddedContact.avatar || "",
      email: newAddedContact.email,
      phone: newAddedContact.phone || "",
      isCreated: true,
      roleId: newAddedContact.role?.value,
      personType: newAddedContact.role?.roleType || "",
      addToTransaction: newAddedContact.addToTransaction || false,
    };
  };

  const onSaveContact = (isEdit, targetValue) => {
    if (isEdit) {
      setIsEditFormOpen({});
      const updatedContact = formatContactData(editingContact);
      setNewContacts((prevContacts) =>
        prevContacts.map((contact) => (contact.value === targetValue ? updatedContact : contact)),
      );
      setEditiongContact({});
    } else {
      setAddContact(false);
      const addedContact = formatContactData(newAddedContact);
      setNewContacts((prevContacts) => [...prevContacts, addedContact]);
      setNewAddedContact({});
    }
  };

  const getValByIds = () => {
    const selectedIds = contacts.map((item) => item.value);
    return ContactsOptions()
      .flatMap((group) => group.options) // Cleaner alternative to reduce
      .filter((contact) => selectedIds.includes(contact.value));
  };

  const onAddContact = () => {
    setNewAddedContact({});
    setAddContact(true);
  };

  const handleDelete = (idToRemove, isCreated) => {
    if (isCreated) {
      setNewContacts(newContacts.filter((contact) => contact.value !== idToRemove));
    } else {
      setContact(contacts.filter((contact) => contact.value !== idToRemove));
    }
  };

  const handleEditClick = (contact) => {
    setIsEditFormOpen({ id: contact.value });
    setEditiongContact(contact);
  };

  return (
    <div>
      {!addContact ? (
        <Dropdown
          isMulti
          controlShouldRenderValue={false}
          multiShouldRenderBelowSelect={false}
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{
            GroupHeading: CheckboxGroupHeading,
            Option: CheckboxOption,
          }}
          isSearchable
          label={
            <>
              <div className="tw-flex tw-w-full tw-justify-between">
                {contactType === "co-op"
                  ? "Cooperating Agent(s)"
                  : contactType === "seller"
                    ? "Seller(s)"
                    : "Additional Contacts"}
                <div>
                  <TextButton className="tw-p-[0px]" disabled={isEditFormOpen?.id} onClick={onAddContact}>
                    <Plus size="s" /> Add Contact Info
                  </TextButton>
                </div>
              </div>
            </>
          }
          placeholder={
            contactType === "co-op"
              ? "Is there an agent on the other side of this transaction?"
              : contactType === "seller"
                ? "Select"
                : "Clients, other team members, etc."
          }
          menuShouldComeToFront
          maxVisibleOptions={7.5}
          options={ContactsOptions()}
          name="contacts"
          value={getValByIds() || []}
          onChange={(option) => setContact(option)}
        />
      ) : (
        <AddAdditionalContact
          roleOptions={roleOptions}
          setNewContact={setNewAddedContact}
          newContact={newAddedContact}
          hideAddContactSection={() => setAddContact(false)}
          onSaveContact={() => onSaveContact(false)}
          contactType={contactType}
        />
      )}
      {(newContacts.length > 0 || contacts.length > 0) && (
        <div className="tw-pt-[8px]">
          {[...contacts, ...newContacts].map((agent, i) => (
            <>
              {agent?.isCreated && isEditFormOpen?.id === agent?.value ? (
                <AddAdditionalContact
                  key={i}
                  roleOptions={roleOptions}
                  setNewContact={setEditiongContact}
                  newContact={editingContact}
                  hideAddContactSection={() => setIsEditFormOpen({})}
                  onSaveContact={() => onSaveContact(true, agent?.value)}
                  contactType={contactType}
                  isEdit
                />
              ) : (
                <ContactInfoCard
                  key={i}
                  name={agent?.name}
                  avatarImg={agent?.avatarImg}
                  role={agent?.roleLabel || ""}
                  email={agent?.email || ""}
                  phone={agent?.phone || ""}
                  canDelete
                  canEdit={agent?.isCreated}
                  handleDelete={() => handleDelete(agent?.value, agent?.isCreated)}
                  handleEditClick={() => handleEditClick(agent)}
                  isEditFormOpen={isEditFormOpen?.id}
                />
              )}
            </>
          ))}
        </div>
      )}
    </div>
  );
};

Contacts.propTypes = {
  collaborators: arrayOf(shape({})),
  clients: arrayOf(shape({})),
  team: arrayOf(shape({})),
  setContact: func,
  contacts: arrayOf(shape({})),
  setNewContacts: func,
  newContacts: arrayOf(shape({})),
  roles: shape({}),
  addContact: bool,
  setAddContact: func,
  contactType: string,
};

Contacts.defaultProps = {
  collaborators: [],
  clients: [],
  team: [],
  contacts: [],
  setContact: null,
  setNewContacts: null,
  newContacts: [],
  roles: {
    client: [],
    collaborator: [],
  },
  setAddContact: null,
  addContact: false,
  contactType: "co-op",
};

const mapStateToProps = (state) => ({
  clients: state.tdpPeopleReducer.people?.listing_clients,
  collaborators: state.tdpPeopleReducer.people?.collaborators,
  team: state.tdpPeopleReducer.people?.team_members,
  roles: {
    client: _.uniqBy(state.tdpPeopleReducer.people.roles?.client, "title"),
    collaborator: _.uniqBy(state.tdpPeopleReducer.people.roles?.collaborator, "title"),
    team_member: _.uniqBy(state.tdpPeopleReducer.people.roles?.team_member, "title"),
  },
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Contacts);
